import { TabView, TabPanel } from 'primereact/tabview';
import GenericTable from '../GenericTable';
import { useState, useMemo } from 'react';
import DialogRenderer from '../dialogs/indexs';
import RowHistoryDialog from '../dialogs/RowHistoryDialog';
import TabChangeBadge from './TabChangeBadge';
import { dateOnlyBodyTemplate } from '../../../../../../utils/dateUtils';
import { usePaymentData } from '../../hooks/usePaymentData';
import { useProfitData } from '../../hooks/useProfitData';
import { useAuthSrcTypeData } from '../../hooks/useAuthSrcTypeData';
import { useRegionalManagerData } from '../../hooks/useRegionalManagerData';
import { useTechnicalSupportResponsibleData } from '../../hooks/useTechnicalSupportResponsibleData';
import { useAddrReportData } from '../../hooks/useAddrReportData';
import { useDealerAgreementTypeData } from '../../hooks/useDealerAgreementTypeData';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { buildTabChangeMap, ChangeTargetType } from '../../../../../../utils/dealerApprovalEnums';
import { useDealerAddressUsersData } from '../../hooks/useDealerAddressUsersData';

const TabsView = ({ dealerAddressBrandId, dealerAddress, model, brand, dealer, tabChanges = [], readOnly = false, approvalContext = false }) => {
    const { th } = useLanguage();
    const [activeIndex, setActiveIndex] = useState(0);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [shouldFetchData, setShouldFetchData] = useState(new Set([0]));
    const [historyState, setHistoryState] = useState({ visible: false, targetType: null, rowId: null, columns: [] });

    const tabChangeMap = useMemo(() => buildTabChangeMap(tabChanges), [tabChanges]);

    const paymentHook = usePaymentData({ dealerAddressBrandId, dealerAddress, pageSize: 10, enabled: shouldFetchData.has(0) });
    const profitHook = useProfitData({
        dealerAddressBrandId,
        dealerAddress,
        model,
        brand,
        pageSize: 10,
        enabled: shouldFetchData.has(1)
    });
    const authSrcTypeHook = useAuthSrcTypeData({ dealerAddressBrandId, dealerAddress, pageSize: 10, enabled: shouldFetchData.has(2) });
    const regionalManagerHook = useRegionalManagerData({ dealerAddressBrandId, dealerAddress, pageSize: 10, enabled: shouldFetchData.has(3) });
    const technicalSupportResponsibleHook = useTechnicalSupportResponsibleData({
        dealerAddressBrandId,
        dealerAddress,
        pageSize: 10,
        enabled: shouldFetchData.has(4)
    });
    const addrReportHook = useAddrReportData({ dealerAddressBrandId, dealer, dealerAddress, pageSize: 10, enabled: shouldFetchData.has(5) });
    const dealerAgreementTypeHook = useDealerAgreementTypeData({ dealerAddressBrandId, dealer, pageSize: 10, enabled: shouldFetchData.has(6) });
    const dealerAddressUsersHook = useDealerAddressUsersData({ dealerAddress, pageSize: 10, enabled: shouldFetchData.has(7) });

    const hooks = [paymentHook, profitHook, authSrcTypeHook, regionalManagerHook, technicalSupportResponsibleHook, addrReportHook, dealerAgreementTypeHook, dealerAddressUsersHook];

    const dealerdata = dealer ? { id: dealer.id, name: dealer.name } : null;

    if (!dealerdata) return <div>{th('common.loading')}...</div>;

    const openDialog = (row) => {
        if (readOnly) return;
        setSelectedRow(row);
        setDialogVisible(true);
    };

    const handleDelete = (row) => {
        if (readOnly) return;
        console.log('Silinecek:', row);
    };

    const handleCreate = () => {
        if (readOnly) return;
        setSelectedRow(null);
        setDialogVisible(true);
    };

    const openHistory = (targetType, row, columns) => {
        if (!row?.id || targetType == null) return;
        setHistoryState({ visible: true, targetType, rowId: row.id, columns: columns || [] });
    };

    const closeHistory = () => setHistoryState({ visible: false, targetType: null, rowId: null, columns: [] });

    const handlePage = (event) => {
        if (hooks[activeIndex]?.setPageNumber) {
            const newPage = event.page + 1;
            hooks[activeIndex].setPageNumber(newPage);
        }
    };

    const onTabChange = (e) => {
        const newIndex = e.index;
        setActiveIndex(newIndex);
        setShouldFetchData((prev) => new Set(prev).add(newIndex));
    };

    const renderGenericTable = (hook, targetType, additionalProps = {}) => {
        if (hook.isLoading || !hook.columns || !Object.prototype.hasOwnProperty.call(hook, 'count')) {
            return <div>{th('common.loading')}...</div>;
        }
        // Backend staged satırları GetList'te döndürür; overlay yok — doğrudan rowState ile render.
        const rows = hook.payments || hook.items || hook.data || [];
        const tabApprovalContext = approvalContext && targetType != null;
        return (
            <GenericTable
                value={rows}
                loading={hook.isLoading}
                columns={hook.columns}
                totalRecords={hook.count}
                handlePage={handlePage}
                currentFirst={(hook.pageNumber - 1) * hook.pageSize}
                onEdit={openDialog}
                onDelete={handleDelete}
                onCreate={handleCreate}
                onHistory={targetType != null ? (row) => openHistory(targetType, row, hook.columns) : undefined}
                dateOnlyBodyTemplate={dateOnlyBodyTemplate}
                approvalContext={tabApprovalContext}
                readOnly={readOnly}
                suppressCloseOut={targetType === ChangeTargetType.REGIONAL_MANAGER}
                {...additionalProps}
            />
        );
    };

    const renderTabHeader = (iconClass, headerKey, count, countClass, targetType) => (
        <span>
            <i className={`${iconClass} p-mr-2`} style={{ marginRight: '0.5rem' }}></i>
            {th(headerKey)}
            {count > 0 && <span className={`ml-2 px-2 py-1 border-round text-xs font-semibold ${countClass}`}>{count}</span>}
            {targetType != null && <TabChangeBadge tabChange={tabChangeMap[targetType]} />}
        </span>
    );

    return (
        <>
            <TabView activeIndex={activeIndex} onTabChange={onTabChange} scrollable={true}>
                <TabPanel header={renderTabHeader('pi pi-wallet', approvalContext ? 'dealerRegistration.tabs.paymentHeader' : 'customerAddressBrand.tabs.header.paymentHeader', activeIndex === 0 ? paymentHook.count : 0, 'bg-blue-100 text-blue-800', ChangeTargetType.PAYMENT)}>
                    {renderGenericTable(paymentHook, ChangeTargetType.PAYMENT)}
                </TabPanel>

                <TabPanel header={renderTabHeader('pi pi-chart-line', approvalContext ? 'dealerRegistration.tabs.profitHeader' : 'customerAddressBrand.tabs.header.profitHeader', activeIndex === 1 ? profitHook.count : 0, 'bg-green-100 text-green-800', ChangeTargetType.PROFIT)}>
                    {renderGenericTable(profitHook, ChangeTargetType.PROFIT)}
                </TabPanel>

                <TabPanel header={renderTabHeader('pi pi-id-card', 'customerAddressBrand.tabs.header.authSrcTypeHeader', activeIndex === 2 ? authSrcTypeHook.count : 0, 'bg-orange-100 text-orange-800', ChangeTargetType.AUTH_SRC_TYPE)}>
                    {renderGenericTable(authSrcTypeHook, ChangeTargetType.AUTH_SRC_TYPE)}
                </TabPanel>

                <TabPanel header={renderTabHeader('pi pi-users', 'customerAddressBrand.tabs.header.regionalManagerHeader', activeIndex === 3 ? regionalManagerHook.count : 0, 'bg-purple-100 text-purple-800', ChangeTargetType.REGIONAL_MANAGER)}>
                    {renderGenericTable(regionalManagerHook, ChangeTargetType.REGIONAL_MANAGER)}
                </TabPanel>

                <TabPanel
                    header={renderTabHeader(
                        'pi pi-user-edit',
                        'customerAddressBrand.tabs.header.technicalSupportHeader',
                        activeIndex === 4 ? technicalSupportResponsibleHook.count : 0,
                        'bg-indigo-100 text-indigo-800',
                        ChangeTargetType.TECHNICAL_SUPPORT
                    )}
                >
                    {renderGenericTable(technicalSupportResponsibleHook, ChangeTargetType.TECHNICAL_SUPPORT)}
                </TabPanel>

                <TabPanel header={renderTabHeader('pi pi-map-marker', 'customerAddressBrand.tabs.header.addrReportHeader', activeIndex === 5 ? addrReportHook.count : 0, 'bg-teal-100 text-teal-800', ChangeTargetType.ADDRESS_REPORT)}>
                    {renderGenericTable(addrReportHook, ChangeTargetType.ADDRESS_REPORT)}
                </TabPanel>

                <TabPanel header={renderTabHeader('pi pi-briefcase', 'customerAddressBrand.tabs.header.dealerAgreementType', activeIndex === 6 ? dealerAgreementTypeHook.count : 0, 'bg-yellow-100 text-yellow-800', ChangeTargetType.AGREEMENT_TYPE)}>
                    {renderGenericTable(dealerAgreementTypeHook, ChangeTargetType.AGREEMENT_TYPE)}
                </TabPanel>

                <TabPanel header={renderTabHeader('pi pi-user-plus', 'customerAddressBrand.tabs.header.dealerAddressUsers', activeIndex === 7 ? dealerAddressUsersHook.count : 0, 'bg-gray-100 text-gray-800', null)}>
                    {renderGenericTable(dealerAddressUsersHook)}
                </TabPanel>
            </TabView>

            <DialogRenderer
                activeTab={activeIndex}
                visible={dialogVisible}
                data={selectedRow}
                dealer={dealer}
                onHide={() => setDialogVisible(false)}
                hooks={hooks}
                refreshDealerAddressUsers={dealerAddressUsersHook.refetchData}
                dealerAddress={dealerAddress}
                defaultCurrency={paymentHook.dealerCurrency}
                brand={brand}
            />

            <RowHistoryDialog visible={historyState.visible} onHide={closeHistory} targetType={historyState.targetType} rowId={historyState.rowId} columns={historyState.columns} />
        </>
    );
};

export default TabsView;
